import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Mascot from '../components/site/Mascot'
import SiteShell, { CONTAINER } from '../components/site/SiteShell'
import BazaarButton from '../components/site/BazaarButton'
import { webInstallState } from '../lib/download'
import { GOOD_FROM, MID_FROM } from '../lib/scoreBand'
import { localizedNumber } from '../lib/site'
import { isIos, isStandalone, useInstallStore } from '../store/installStore'

/**
 * The public landing (design "Tooti Web", the `landing` page): hero with
 * the phone mock, the three steps, the word-colour section, six features,
 * pricing, the download band, and the FAQ — each section tagged
 * `data-guide` so the shell's parrot can comment on the one in view.
 *
 * Left out of the design on purpose, not by oversight: the three
 * testimonials (its own note: "placeholder copy — replace with real user
 * reviews"), the newsletter box (no list exists to subscribe to), and the
 * Google Play button (the app is not on Google Play; the Android paths are
 * the APK channel the download page fronts and, on the Persian site only,
 * the Café Bazaar listing — `BazaarButton` in the download band).
 */

const H2 = 'font-display text-[clamp(28px,6vw,42px)] font-extrabold tracking-[-0.9px]'
const LEAD = 'text-base leading-[1.85] text-ink-2'
const CARD = 'site-card site-card-lift flex flex-col gap-3.5 rounded-3xl border-2 border-line bg-surface p-5 sm:p-[26px]'
const SECTION_Y = 'py-[clamp(38px,7vw,64px)]'
const GREEN_CTA =
  'site-btn inline-flex h-[60px] items-center justify-center gap-2.5 rounded-[18px] bg-site-good px-[30px] font-display text-[19px] font-extrabold text-white [--press:var(--color-good-deep)]'
const PLAIN_CTA =
  'site-btn inline-flex h-[60px] items-center justify-center rounded-[18px] border-2 border-line-2 bg-surface px-[26px] font-display text-[19px] font-bold text-ink [--press:var(--color-line-2)]'
const PLAN_CTA = 'site-btn mt-auto flex h-14 items-center justify-center rounded-2xl font-display text-[17px] font-extrabold'

const STEP_COLORS = [
  { bg: 'bg-sky', press: 'shadow-[0_4px_0_var(--color-sky-deep)]', tilt: '-rotate-1' },
  { bg: 'bg-coral', press: 'shadow-[0_4px_0_var(--color-coral-deep)]', tilt: 'rotate-1' },
  { bg: 'bg-violet', press: 'shadow-[0_4px_0_var(--color-violet-deep)]', tilt: '-rotate-1' },
]

const FEATURE_ICONS = [
  { shape: 'rounded-full bg-accent shadow-[0_3px_0_var(--color-accent-deep)]', card: 'bg-accent-tint', delay: '0s' },
  { shape: 'rounded-[14px] bg-sky shadow-[0_3px_0_var(--color-sky-deep)]', card: 'bg-surface', delay: '.2s' },
  { shape: 'rounded-full bg-coral shadow-[0_3px_0_var(--color-coral-deep)]', card: 'bg-surface', delay: '.1s' },
  { shape: 'rounded-[14px] bg-violet shadow-[0_3px_0_var(--color-violet-deep)]', card: 'bg-surface', delay: '.3s' },
  { shape: 'rounded-full bg-site-good shadow-[0_3px_0_var(--color-good-deep)]', card: 'bg-surface', delay: '.15s' },
  { shape: 'rounded-[14px] bg-brand-600 shadow-[0_3px_0_var(--color-brand-deep)]', card: 'bg-surface', delay: '.25s' },
]

// The sample sentence of the design's score card. English lesson text in
// both languages — the lesson is always English (docs/design-system.md).
const SAMPLE_LESSON = 'A morning at the airport'
const SAMPLE_WORDS = [
  ['Could', 'good'],
  ['you', 'good'],
  ['check', 'mid'],
  ['my', 'good'],
  ['luggage', 'bad'],
  ['before', 'good'],
  ['the', 'muted'],
  ['flight', 'mid'],
]
const WORD_TONE = {
  good: 'bg-site-good-tint text-good-ink',
  mid: 'bg-site-warn-tint text-warn-ink',
  bad: 'bg-site-bad-tint font-bold text-bad-ink shadow-[0_0_0_2px_var(--color-bad)]',
  muted: 'bg-muted-tint text-ink-2',
}

const INSTALL_HINT = { ios: 'hintIos', manual: 'hintManual', standalone: 'hintStandalone', installed: 'hintInstalled' }

/**
 * The hero title, one word at a time (the design's `t-word` stagger).
 * Inline, not a flex row: the three title parts must wrap as one sentence,
 * or a short trailing word ("و") is stranded on a line of its own on phones.
 */
function Words({ text, from = 0 }) {
  return text.split(' ').map((word, index) => (
    <span key={`${word}-${index}`}>
      <span
        className="inline-block"
        style={{ animation: `t-word .6s ${(from + index * 0.09).toFixed(2)}s cubic-bezier(.22,1.4,.36,1) both` }}
      >
        {word}
      </span>{' '}
    </span>
  ))
}

function PhoneMock() {
  const { t } = useTranslation('site')
  const bars = [0, 0.12, 0.24, 0.36, 0.48, 0.6, 0.72, 0.84]
  return (
    <div className="relative">
      <div
        className="w-[264px] max-w-full rounded-[42px] sm:w-[306px] bg-ink p-2.5 shadow-[0_30px_60px_var(--color-hero-glow)]"
        style={{ animation: 't-float 7s ease-in-out infinite' }}
      >
        <div className="flex flex-col gap-3 overflow-hidden rounded-[34px] bg-ground px-4 pb-[18px] pt-5">
          <div className="flex items-center gap-2.5">
            <div className="flex flex-grow flex-col gap-0.5">
              <span className="font-display text-[21px] font-extrabold tracking-[-0.3px]">{t('hero.phone.greeting')}</span>
              <span className="text-xs text-ink-2">{t('hero.phone.left')}</span>
            </div>
            <span
              className="flex items-center gap-1.5 rounded-full bg-accent px-3 py-2 font-display text-sm font-extrabold text-on-accent shadow-[0_3px_0_var(--color-accent-deep)]"
              style={{ animation: 't-bob 2.6s ease-in-out infinite' }}
            >
              {t('hero.phone.coins')}
            </span>
          </div>
          <div className="flex flex-col gap-3 rounded-[20px] bg-[linear-gradient(135deg,#2B5A96_0%,#1E4372_100%)] p-4 shadow-[0_5px_0_#1E4372]">
            <span className="text-[11px] font-bold tracking-[0.4px] text-[#A9C4E4]">{t('hero.phone.continue')}</span>
            <span dir="ltr" className="text-start font-serif text-[15.5px] font-medium text-white sm:text-lg">
              {t('hero.phone.lesson')}
            </span>
            <div className="h-2.5 overflow-hidden rounded-full bg-white/20">
              <div
                className="h-2.5 w-[62%] rounded-full bg-[linear-gradient(90deg,var(--color-accent),#FFE37A)] [background-size:200%_100%]"
                style={{ animation: 't-shimmer 2.4s linear infinite' }}
              />
            </div>
            <span className="text-[11.5px] text-[#A9C4E4]">{t('hero.phone.progress')}</span>
          </div>
          <div className="flex flex-col gap-2.5 rounded-[20px] border-2 border-line bg-surface p-3.5">
            <span className="text-xs font-bold text-ink-2">{t('hero.phone.listening')}</span>
            <div className="flex h-11 items-center gap-[5px]">
              {bars.map((delay, index) => (
                <span
                  key={delay}
                  className={`h-full flex-grow rounded-full ${index % 3 === 2 ? 'bg-brand-600' : 'bg-sky'}`}
                  style={{ transformOrigin: 'center', animation: `t-wave 1s ${delay}s ease-in-out infinite` }}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-[20px] border-2 border-line bg-surface px-3.5 py-3">
            <span className="relative flex size-[42px] shrink-0 items-center justify-center rounded-full bg-coral shadow-[0_3px_0_var(--color-coral-deep)]">
              <span className="absolute inset-0 rounded-full border-[3px] border-coral" style={{ animation: 't-ring 1.8s ease-out infinite' }} />
              <span className="size-[13px] rounded bg-white" />
            </span>
            <span className="font-display text-base font-bold">{t('hero.phone.record')}</span>
          </div>
        </div>
      </div>
      <span
        className="absolute -end-[26px] -top-[26px] rounded-2xl border-2 border-line bg-surface px-3.5 py-2.5 font-display text-[15px] font-extrabold text-good-ink shadow-[0_4px_0_var(--color-line)]"
        style={{ animation: 't-bob 3.4s ease-in-out infinite' }}
      >
        {t('hero.phone.xp')}
      </span>
    </div>
  )
}

function Hero() {
  const { t } = useTranslation('site')
  const chips = t('hero.chips', { returnObjects: true })
  const chipTones = ['bg-site-good-tint', 'bg-sky-tint', 'bg-violet-tint']
  return (
    <section data-guide="hero" className="relative overflow-hidden">
      <span className="pointer-events-none absolute left-[6%] top-[8%] size-[72px] rounded-full sm:size-[120px] bg-accent-tint" style={{ animation: 't-float 9s ease-in-out infinite' }} />
      <span className="pointer-events-none absolute bottom-[14%] right-[4%] size-[180px] rounded-full bg-sky-tint" style={{ animation: 't-float 12s ease-in-out infinite reverse' }} />
      <span className="pointer-events-none absolute left-[44%] top-[52%] size-3.5 rounded-full bg-coral" style={{ animation: 't-sparkle 3s ease-in-out infinite' }} />
      <div
        className={`${CONTAINER} relative grid items-center gap-[clamp(26px,5vw,48px)] pb-[clamp(28px,5vw,44px)] pt-[clamp(30px,6vw,58px)] [grid-template-columns:repeat(auto-fit,minmax(min(320px,100%),1fr))]`}
      >
        <div className="flex min-w-0 flex-col gap-4 sm:gap-[22px]" style={{ animation: 't-slide-left .6s ease-out both' }}>
          <span
            className="flex origin-top-left items-center gap-[9px] self-start rounded-full bg-accent px-3.5 py-2 font-display text-sm font-bold sm:px-4 sm:py-[9px] sm:text-[15px] text-on-accent shadow-[0_3px_0_var(--color-accent-deep)] rtl:origin-top-right"
            style={{ animation: 't-swing 4s ease-in-out infinite' }}
          >
            <span className="size-[9px] rounded-full bg-on-accent" />
            {t('hero.badge')}
          </span>
          <h1 className="font-display text-[clamp(30px,7.6vw,62px)] font-extrabold leading-[1.2] tracking-[-0.8px] [text-wrap:pretty] sm:tracking-[-1.4px]">
            <Words text={t('hero.title1')} />
            <span className="relative inline-block whitespace-nowrap text-brand-600" style={{ animation: 't-word .6s .3s cubic-bezier(.22,1.4,.36,1) both' }}>
              <span className="relative inline-block" style={{ animation: 't-shout 2.6s 1.1s ease-in-out infinite' }}>
                {t('hero.title2')}
              </span>
              <span
                className="absolute inset-x-0 bottom-0.5 -z-10 h-3 origin-left rounded-full bg-accent rtl:origin-right"
                style={{ animation: 't-underline .45s .62s cubic-bezier(.22,1.4,.36,1) both' }}
              />
            </span>
            {/* no space before a title3 that opens on punctuation ("—every.") */}
            {/^[\p{L}\p{N}]/u.test(t('hero.title3')) && ' '}
            <Words text={t('hero.title3')} from={0.44} />
          </h1>
          <p className="max-w-[42ch] text-[15.5px] leading-[1.8] text-ink-2 [text-wrap:pretty] sm:text-[17px] sm:leading-[1.85]">{t('hero.lead')}</p>
          <div className="grid grid-cols-[1.4fr_1fr] gap-2.5 sm:flex sm:flex-wrap sm:gap-3">
            <Link to="/download" className={`${GREEN_CTA} max-sm:h-[52px] max-sm:px-4 max-sm:text-[17px]`}>
              {t('hero.cta')}
            </Link>
            <Link to="/auth" className={`${PLAIN_CTA} max-sm:h-[52px] max-sm:px-4 max-sm:text-[17px]`}>
              {t('hero.login')}
            </Link>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-2.5 sm:pt-0.5">
            {chips.map((chip, index) => (
              <span key={chip} className={`rounded-full px-3 py-1.5 text-[13px] font-semibold sm:px-3.5 sm:py-2 sm:text-sm text-ink-2 ${chipTones[index % chipTones.length]}`}>
                {chip}
              </span>
            ))}
          </div>
        </div>
        <div className="flex min-w-0 justify-center" style={{ animation: 't-slide-right .6s .1s ease-out both' }}>
          <PhoneMock />
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  const { t, i18n } = useTranslation('site')
  const language = i18n.resolvedLanguage ?? i18n.language
  const steps = t('how.steps', { returnObjects: true })
  return (
    <section data-guide="how" className={`${CONTAINER} ${SECTION_Y} flex flex-col gap-8`}>
      <div className="site-reveal flex max-w-[50ch] flex-col gap-2.5">
        <h2 className={H2}>{t('how.title')}</h2>
        <p className={LEAD}>{t('how.lead')}</p>
      </div>
      <ol className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(250px,100%),1fr))]">
        {steps.map((step, index) => {
          const color = STEP_COLORS[index % STEP_COLORS.length]
          return (
            <li key={step.title} className={`${CARD} site-reveal hover:${color.tilt}`}>
              <span className={`flex size-14 items-center justify-center rounded-[18px] font-display text-[26px] font-extrabold text-white ${color.bg} ${color.press}`}>
                {localizedNumber(index + 1, language)}
              </span>
              <span className="font-display text-[22px] font-extrabold">{step.title}</span>
              <span className="text-[15px] leading-[1.8] text-ink-2">{step.body}</span>
            </li>
          )
        })}
      </ol>
    </section>
  )
}

function ScoreSection() {
  const { t, i18n } = useTranslation('site')
  const n = (value) => localizedNumber(value, i18n.resolvedLanguage ?? i18n.language)
  return (
    <section data-guide="score" className="border-y-2 border-line bg-surface-2">
      <div className={`${CONTAINER} ${SECTION_Y} grid items-center gap-[clamp(26px,4.6vw,44px)] [grid-template-columns:repeat(auto-fit,minmax(min(310px,100%),1fr))]`}>
        <div className="site-reveal flex min-w-0 flex-col gap-[18px]">
          <h2 className={`${H2} leading-[1.2]`}>{t('score.title')}</h2>
          <p className={`${LEAD} max-w-[44ch] [text-wrap:pretty]`}>{t('score.lead')}</p>
          <ul className="flex flex-col gap-2.5 text-[15px] font-semibold text-ink-2">
            <li className="flex items-center gap-2.5">
              <span className="size-[18px] rounded-md bg-site-good shadow-[0_2px_0_var(--color-good-deep)]" />
              {t('score.legendGood', { from: n(GOOD_FROM) })}
            </li>
            <li className="flex items-center gap-2.5">
              <span className="size-[18px] rounded-md bg-warn shadow-[0_2px_0_var(--color-warn-ink)]" />
              {t('score.legendMid', { from: n(MID_FROM), to: n(GOOD_FROM - 1) })}
            </li>
            <li className="flex items-center gap-2.5">
              <span className="size-[18px] rounded-md bg-bad shadow-[0_2px_0_var(--color-bad-ink)]" />
              {t('score.legendBad', { from: n(MID_FROM) })}
            </li>
          </ul>
        </div>
        <div className="site-reveal flex min-w-0 flex-col gap-[22px] rounded-[28px] border-2 border-line bg-surface p-5 shadow-[0_8px_0_var(--color-line)] sm:p-7">
          <div className="flex items-center gap-[18px]">
            <div
              className="relative flex size-[110px] shrink-0 items-center justify-center rounded-full [background:conic-gradient(var(--color-site-good)_0_72%,var(--color-track)_72%_100%)]"
              style={{ animation: 't-pop .6s ease-out both' }}
            >
              <span className="absolute -inset-1.5 rounded-full border-[3px] border-site-good opacity-35" style={{ animation: 't-ring 2.6s ease-out infinite' }} />
              <div className="flex size-[84px] flex-col items-center justify-center rounded-full bg-surface">
                <span className="font-display text-[32px] font-extrabold tracking-[-1px]" style={{ animation: 't-count .5s .2s ease-out both' }}>
                  {n(72)}
                </span>
                <span className="text-[10px] text-ink-2">{t('score.of')}</span>
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="font-display text-2xl font-extrabold text-good-ink">{t('score.praise')}</span>
              <span dir="ltr" className="text-start font-serif text-base text-ink-2">
                {SAMPLE_LESSON}
              </span>
            </div>
          </div>
          <div dir="ltr" className="flex flex-wrap gap-2 font-serif">
            {SAMPLE_WORDS.map(([word, tone], index) => (
              <span
                key={word}
                className={`rounded-xl px-3 py-2 text-base font-semibold ${WORD_TONE[tone]}`}
                style={{ animation: `t-pop .4s ${(0.05 + index * 0.07).toFixed(2)}s both` }}
              >
                {word}
              </span>
            ))}
          </div>
          <div className="flex flex-col gap-1.5 rounded-[18px] bg-surface-2 px-[18px] py-4">
            <span dir="ltr" className="text-start font-serif text-[17px] font-semibold">
              luggage
            </span>
            <span className="text-sm leading-[1.85] text-ink-2">{t('score.tip')}</span>
          </div>
        </div>
      </div>
    </section>
  )
}

function Features() {
  const { t } = useTranslation('site')
  const items = t('features.items', { returnObjects: true })
  return (
    <section data-guide="features" className={`${CONTAINER} ${SECTION_Y} flex flex-col gap-[30px]`}>
      <div className="site-reveal flex max-w-[50ch] flex-col gap-2.5">
        <h2 className={H2}>{t('features.title')}</h2>
        <p className={LEAD}>{t('features.lead')}</p>
      </div>
      <ul className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(260px,100%),1fr))]">
        {items.map((item, index) => {
          const icon = FEATURE_ICONS[index % FEATURE_ICONS.length]
          return (
            <li key={item.title} className={`site-card site-card-lift flex flex-col gap-3 rounded-3xl border-2 border-line p-5 sm:p-6 ${icon.card}`}>
              <span className={`size-12 ${icon.shape}`} style={{ animation: `t-bob ${3 + (index % 4) * 0.2}s ${icon.delay} ease-in-out infinite` }} />
              <span className="font-display text-xl font-extrabold">{item.title}</span>
              <span className="text-[14.5px] leading-[1.8] text-ink-2">{item.body}</span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function Pricing() {
  const { t } = useTranslation('site')
  const premiumPerks = t('pricing.premium.perks', { returnObjects: true })
  const standardPerks = t('pricing.standard.perks', { returnObjects: true })
  const price = 'font-display text-[clamp(34px,7vw,44px)] font-extrabold tracking-[-1.4px]'
  return (
    <section data-guide="pricing" className="border-y-2 border-line bg-surface-2">
      <div className={`${CONTAINER} ${SECTION_Y} flex flex-col gap-[30px]`}>
        <div className="site-reveal flex max-w-[50ch] flex-col gap-2.5">
          <h2 className={H2}>{t('pricing.title')}</h2>
          <p className={LEAD}>{t('pricing.lead')}</p>
        </div>
        <div className="grid items-stretch gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(270px,100%),1fr))]">
          <div className="site-card site-card-lift flex flex-col gap-4 rounded-[26px] border-2 border-line bg-surface p-5 sm:p-7">
            <span className="font-display text-lg font-extrabold text-good-ink">{t('pricing.trial.name')}</span>
            <span className={price}>{t('pricing.trial.price')}</span>
            <span className="text-[14.5px] leading-[1.8] text-ink-2">{t('pricing.trial.body')}</span>
            <span className="text-[13.5px] leading-[1.8] text-ink-2">{t('pricing.trial.note')}</span>
            <Link to="/auth" state={{ from: '/paywall' }} className={`${PLAN_CTA} bg-surface-2 text-ink [--press:var(--color-line-2)]`}>
              {t('pricing.trial.cta')}
            </Link>
          </div>
          <div className="relative flex flex-col gap-4 rounded-[26px] border-[3px] border-brand-600 bg-surface p-5 shadow-[0_8px_0_var(--color-brand-600)] transition hover:-translate-y-2 sm:p-7">
            <span
              className="absolute -top-4 start-6 rounded-full bg-accent px-3.5 py-[7px] font-display text-sm font-extrabold text-on-accent shadow-[0_3px_0_var(--color-accent-deep)]"
              style={{ animation: 't-bob 3s ease-in-out infinite' }}
            >
              {t('pricing.premium.popular')}
            </span>
            <span className="font-display text-lg font-extrabold text-brand-600">{t('pricing.premium.name')}</span>
            <div className="flex flex-col gap-1">
              <span className={price}>{t('pricing.premium.price')}</span>
              <span className="text-[13.5px] text-ink-2">{t('pricing.premium.billed')}</span>
            </div>
            <ul className="flex flex-col gap-2.5 text-[14.5px] text-ink-2">
              {premiumPerks.map((perk, index) => (
                <li key={perk} className={index === 1 ? 'font-bold text-brand-600' : undefined}>
                  {perk}
                </li>
              ))}
            </ul>
            <Link to="/auth" state={{ from: '/paywall?plan=premium' }} className={`${PLAN_CTA} bg-brand-600 text-white [--press:var(--color-brand-deep)] dark:text-slate-900`}>
              {t('pricing.premium.cta')}
            </Link>
          </div>
          <div className="site-card site-card-lift flex flex-col gap-4 rounded-[26px] border-2 border-line bg-surface p-5 sm:p-7">
            <span className="font-display text-lg font-extrabold text-ink-2">{t('pricing.standard.name')}</span>
            <span className={price}>{t('pricing.standard.price')}</span>
            <ul className="flex flex-col gap-2.5 text-[14.5px] text-ink-2">
              {standardPerks.map((perk) => (
                <li key={perk}>{perk}</li>
              ))}
              <li className="text-ink-2">{t('pricing.standard.missing')}</li>
            </ul>
            <Link to="/auth" state={{ from: '/paywall?plan=standard' }} className={`${PLAN_CTA} bg-surface-2 text-ink [--press:var(--color-line-2)]`}>
              {t('pricing.standard.cta')}
            </Link>
          </div>
        </div>
        <span className="text-[13px] leading-[1.9] text-ink-2">{t('pricing.disclaimer')}</span>
      </div>
    </section>
  )
}

/** Which install path this browser gets — decided once, from the store and the device (as the download page does). */
function useWebInstall() {
  const deferredEvent = useInstallStore((s) => s.deferredEvent)
  const installed = useInstallStore((s) => s.installed)
  const [device] = useState(() => ({ standalone: isStandalone(), ios: isIos() }))
  return webInstallState({ ...device, installed, canPrompt: Boolean(deferredEvent) })
}

function DownloadBand() {
  const { t } = useTranslation('site')
  const state = useWebInstall()
  const prompt = useInstallStore((s) => s.prompt)
  const [hint, setHint] = useState('')

  async function installWeb() {
    if (state === 'prompt') {
      const outcome = await prompt()
      setHint(outcome === 'accepted' ? '' : t('download.hintManual'))
      return
    }
    setHint(t(`download.${INSTALL_HINT[state]}`))
  }

  return (
    <section
      id="download"
      data-guide="download"
      className="relative overflow-hidden border-y-2 border-[#1E4372] bg-[linear-gradient(135deg,#2B5A96_0%,#1E4372_100%)] text-white"
    >
      <span className="pointer-events-none absolute -left-[30px] -top-10 size-[180px] rounded-full bg-white/[.07]" style={{ animation: 't-float 11s ease-in-out infinite' }} />
      <span className="pointer-events-none absolute -bottom-[50px] -right-5 size-[220px] rounded-full bg-white/[.06]" style={{ animation: 't-float 14s ease-in-out infinite reverse' }} />
      <div className={`${CONTAINER} relative flex flex-col items-center gap-[26px] py-[clamp(38px,7vw,62px)] text-center`}>
        <div style={{ animation: 't-float 5s ease-in-out infinite' }}>
          <Mascot size={104} variant="white" />
        </div>
        <h2 className="max-w-[22ch] font-display text-[clamp(28px,6.4vw,46px)] font-extrabold leading-[1.15] tracking-[-1.2px]">{t('download.title')}</h2>
        <p className="max-w-[48ch] text-base leading-[1.85] text-[#D8E4F3]">{t('download.lead')}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/download"
            className="site-btn inline-flex h-[60px] items-center justify-center gap-2.5 rounded-[18px] bg-white px-[26px] font-display text-[17px] font-extrabold text-[#1E4372] [--press:rgba(0,0,0,.25)]"
          >
            {t('download.android')}
          </Link>
          <BazaarButton className="site-btn inline-flex h-[60px] items-center justify-center gap-2.5 rounded-[18px] border-2 border-white/70 px-[26px] font-display text-[17px] font-extrabold text-white [--press:rgba(0,0,0,.25)]" />
          <button
            type="button"
            onClick={installWeb}
            className="site-btn inline-flex h-[60px] items-center justify-center rounded-[18px] bg-accent px-[26px] font-display text-[17px] font-extrabold text-on-accent [--press:var(--color-accent-deep)]"
          >
            {t('download.web')}
          </button>
        </div>
        <div className="flex max-w-[640px] flex-col gap-2.5 rounded-[22px] border-2 border-white/25 bg-white/[.12] p-[22px] text-start">
          <span className="font-display text-[19px] font-extrabold">{t('download.webCardTitle')}</span>
          <span className="text-[14.5px] leading-[1.85] text-[#D8E4F3]">{t('download.webCardBody')}</span>
          {hint && (
            <span role="status" className="text-[13.5px] font-bold text-accent" style={{ animation: 't-pop .4s ease-out both' }}>
              {hint}
            </span>
          )}
        </div>
      </div>
    </section>
  )
}

function Faq() {
  const { t } = useTranslation('site')
  const items = t('faq.items', { returnObjects: true })
  const [open, setOpen] = useState(() => items.map(() => false))
  const toggle = (index) => setOpen((prev) => prev.map((value, i) => (i === index ? !value : value)))
  return (
    <section data-guide="faq" className={`mx-auto w-full max-w-[780px] px-4 sm:px-5 ${SECTION_Y} flex flex-col gap-[26px]`}>
      <h2 className={H2}>{t('faq.title')}</h2>
      <div className="flex flex-col gap-3">
        {items.map((item, index) => {
          const expanded = Boolean(open[index])
          const id = `faq-${index}`
          return (
            <div key={item.q} className="overflow-hidden rounded-[20px] border-2 border-line bg-surface shadow-[0_5px_0_var(--color-line)]">
              <button
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={expanded}
                aria-controls={id}
                className="flex w-full items-center justify-between gap-3 px-[22px] py-5 text-start font-display text-lg font-bold text-ink"
              >
                <span>{item.q}</span>
                <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xl text-brand-600">
                  {expanded ? '−' : '+'}
                </span>
              </button>
              {expanded && (
                <p id={id} className="px-[22px] pb-[22px] text-[15px] leading-[1.85] text-ink-2" style={{ animation: 't-rise .3s ease-out both' }}>
                  {item.a}
                </p>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default function Landing() {
  const { t } = useTranslation('site')
  return (
    <SiteShell title={t('pageTitle.landing')} guide>
      <div className="flex flex-col">
        <Hero />
        <HowItWorks />
        <ScoreSection />
        <Features />
        <Pricing />
        <DownloadBand />
        <Faq />
      </div>
    </SiteShell>
  )
}
